import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface ExtensionImportDetail {
    blob: Blob
    mouseData: Array<{ t: number; x: number; y: number; type: 'move' | 'click' | 'dwell' }>
    screenWidth: number
    screenHeight: number
    duration: number
}

export function useExtensionBridge(): void {
    const navigate = useNavigate()

    useEffect(() => {
        const doImport = async (detail: ExtensionImportDetail) => {
            // Clear the window global so retries don't re-trigger
            delete (window as any)['__srPendingImport']

            try {
                // Lazy import stores to avoid circular deps
                const [{ useRecordingStore }, { useEditorStore }] = await Promise.all([
                    import('@/store/recording-store'),
                    import('@/store/editor-store'),
                ])

                const recordingStore = useRecordingStore.getState()
                const editorStore = useEditorStore.getState()

                editorStore.reset()
                recordingStore.reset()

                recordingStore.setBlob(detail.blob)
                if (recordingStore.setMouseData) recordingStore.setMouseData(detail.mouseData ?? [])
                if (recordingStore.setScreenDimensions) {
                    recordingStore.setScreenDimensions(detail.screenWidth || 1920, detail.screenHeight || 1080)
                }
                recordingStore.setDuration(detail.duration || 0)
                recordingStore.setExtension(true)
                recordingStore.setStatus('stopped')

                editorStore.setDuration(detail.duration || 0)
                if (editorStore.initClips) editorStore.initClips(detail.duration || 0)

                if (detail.mouseData && detail.mouseData.length > 0 && editorStore.generateAutoZoom) {
                    editorStore.generateAutoZoom(detail.mouseData as any, detail.screenWidth || 1920, detail.screenHeight || 1080)
                }

                navigate('/editor')
            } catch (e) {
                console.error('[useExtensionBridge] Import failed:', e)
            }
        }

        // Check for data already set by content bridge before React mounted
        const pending = (window as any)['__srPendingImport'] as ExtensionImportDetail | undefined
        if (pending) doImport(pending)

        const handleEvent = (e: Event) => doImport((e as CustomEvent<ExtensionImportDetail>).detail)
        window.addEventListener('screenrecording-extension-import', handleEvent)
        return () => window.removeEventListener('screenrecording-extension-import', handleEvent)
    }, [navigate])
}
